import { useState, useEffect } from "react";
import { Menu, X, Search } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/wave-wire-logo.jpg";
import weTalkSvg from "@/assets/we-talk.svg";
import SearchDialog from "./SearchDialog";

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { label: "Portfolio", path: "/portfolio" },
    { label: "Process", path: "/how-it-works-web-design-process" },
    { label: "Pricing", path: "/services-pricing" },
  ];

  return (
    <>
      {/* Announcement Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-accent text-accent-foreground py-2 px-4 text-center text-xs sm:text-sm font-bold tracking-wide">
        🔥 Special: Bring me your last website quote and I will chop it in half!
      </div>
      <nav
        className={`fixed top-[36px] left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? "bg-background/80 backdrop-blur-xl border-b border-border"
            : "bg-transparent"
        } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"}`}
      >
        <div className="container mx-auto px-6 py-5 flex items-center justify-between relative">
          {/* Mobile: Logo on left */}
          <Link
            to="/"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="flex items-center gap-3 group md:hidden"
          >
            <img
              src={logo}
              alt="Wave & Wire"
              width={40}
              height={40}
              fetchPriority="high"
              className="h-10 w-10 rounded-full object-cover ring-2 ring-primary/50 transition-all duration-300 group-hover:ring-primary group-hover:scale-110"
            />
          </Link>

          {/* Desktop/Tablet: Left - Contact (md+) & Search (lg+ only) */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/contact"
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-bold tracking-widest uppercase transition-all duration-200 hover:scale-105 hover:shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)] active:scale-95"
            >
              Contact
            </Link>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="hidden lg:flex items-center justify-center w-9 h-9 bg-muted/50 border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all"
              aria-label="Search"
            >
              <Search size={16} />
            </button>
          </div>

          {/* Desktop/Tablet: Center - Logo & Business Name */}
          <Link
            to="/"
            onClick={(e) => {
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="hidden md:flex items-center gap-4 group absolute left-1/2 -translate-x-1/2"
          >
            <img
              src={logo}
              alt="Wave & Wire"
              width={40}
              height={40}
              fetchPriority="high"
              className="h-9 w-9 lg:h-10 lg:w-10 rounded-full object-cover ring-2 ring-primary/50 transition-all duration-300 group-hover:ring-primary group-hover:scale-110"
            />
            <span className="text-2xl lg:text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-foreground via-primary to-foreground drop-shadow-[0_0_10px_hsl(var(--primary)/0.3)] transition-all duration-300 group-hover:drop-shadow-[0_0_20px_hsl(var(--primary)/0.6)]">
              WAVE & WIRE
            </span>
          </Link>

          {/* Mobile: Center SVG - appears when navbar has background */}
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center justify-center md:hidden z-10 pointer-events-none w-[calc(100%-10rem)]">
            <img
              src={weTalkSvg}
              alt="We Talk"
              width={80}
              height={64}
              className={`h-16 sm:h-20 w-auto max-w-full transition-all duration-500 will-change-transform ${
                isScrolled
                  ? "opacity-100 translate-y-0 animate-[pulse-glow_2s_ease-in-out_infinite]"
                  : "opacity-0 -translate-y-8"
              }`}
            />
          </div>

          {/* Desktop Only: Right - Navigation Links */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item, index) => (
              <Link
                key={item.label}
                to={item.path}
                className={`relative text-sm font-medium tracking-widest uppercase transition-colors duration-300 group ${
                  location.pathname === item.path 
                    ? "text-primary" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-accent transition-all duration-300 ${
                  location.pathname === item.path ? "w-full" : "w-0 group-hover:w-full"
                }`} />
              </Link>
            ))}
          </div>

          {/* Tablet: Right - Search & Hamburger Menu (md to lg) */}
          <div className="hidden md:flex lg:hidden items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="relative z-50 w-10 h-10 flex items-center justify-center text-foreground hover:text-primary transition-colors"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            <button
              className="relative z-50 w-10 h-10 flex items-center justify-center text-foreground hover:text-primary transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <Menu 
                size={24} 
                className={`absolute transition-all duration-300 ${
                  isMobileMenuOpen ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100"
                }`} 
              />
              <X 
                size={24} 
                className={`absolute transition-all duration-300 ${
                  isMobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"
                }`} 
              />
            </button>
          </div>

          {/* Mobile: Right - Search & Menu Buttons */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="relative z-50 w-10 h-10 flex items-center justify-center text-foreground hover:text-primary transition-colors"
              aria-label="Search"
            >
              <Search size={22} />
            </button>
            <button
              className="relative z-50 w-10 h-10 flex items-center justify-center"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <Menu 
                size={24} 
                className={`absolute transition-all duration-300 ${
                  isMobileMenuOpen ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100"
                }`} 
              />
              <X 
                size={24} 
                className={`absolute transition-all duration-300 ${
                  isMobileMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"
                }`} 
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile/Tablet Menu Overlay - Full Screen */}
      <div
        className={`lg:hidden fixed inset-0 w-screen h-screen z-[9999] bg-black transition-all duration-300 ease-in-out ${
          isMobileMenuOpen 
            ? "opacity-100 visible" 
            : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ top: 0, left: 0 }}
      >
        {/* Close button */}
        <button
          className="absolute top-5 right-6 w-10 h-10 flex items-center justify-center text-white hover:text-primary transition-colors"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <X size={28} />
        </button>

        {/* Logo at top */}
        <Link
          to="/"
          className="absolute top-5 left-6"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <img
            src={logo}
            alt="Wave & Wire"
            width={40}
            height={40}
            loading="lazy"
            className="h-10 w-10 rounded-full object-cover"
          />
        </Link>

        {/* Centered Navigation Links */}
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navItems.map((item, index) => (
            <Link
              key={item.label}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`text-xl font-semibold tracking-widest uppercase transition-all duration-300 ${
                location.pathname === item.path 
                  ? "text-primary" 
                  : "text-white hover:text-primary"
              } ${
                isMobileMenuOpen
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
              style={{ 
                transitionDelay: isMobileMenuOpen ? `${index * 75 + 100}ms` : "0ms"
              }}
            >
              {item.label}
            </Link>
          ))}
          
          {/* Contact Button in Mobile Menu */}
          <Link
            to="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className={`mt-4 px-8 py-3 bg-primary text-primary-foreground text-lg font-bold tracking-widest uppercase transition-all duration-300 ${
              isMobileMenuOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-4"
            }`}
            style={{ 
              transitionDelay: isMobileMenuOpen ? `${navItems.length * 75 + 100}ms` : "0ms"
            }}
          >
            Contact
          </Link>
        </div>
      </div>

      {/* Search Dialog */}
      <SearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </>
  );
};

export default Navbar;
